import { reactive, toRaw } from "vue";
import { OnboardingProduct, type OnboardingStatus } from '@/models/onboarding-status.model';
import {
  OnboardingVerificationItemName, OnboardingVerificationItemOutcome, OnboardingVerificationItemStatus,
} from '@/models/onboarding-verification.model';
import type { Business } from '@/models/business.model';
import onboardingService from '@/services/onboarding.service';
import serverTimeService from '@/services/server-time.service';

import type { OnboardingApiError } from '@/api/errors';

const OnboardedProductUrl = {
  [OnboardingProduct.Factoring]: import.meta.env.VITE_FACTORING_URL,
  [OnboardingProduct.FleetCard]: import.meta.env.VITE_FLEETCARD_URL,
};

export interface OnboardingStatusStoreState {
  error: Error | null,
  isInitialized: boolean;
  isLoading: boolean,
  onboardingStatus: null | OnboardingStatus,
}

export interface OnboardingStatusStore {
  business: Business | null,
  error: Error | null,
  getElapsedSecondsSinceSignup: typeof getElapsedSecondsSinceSignup
  getNextPage: typeof getNextPage,
  getProductUrl: typeof getProductUrl,
  getSignupVerificationItem: typeof getSignupVerificationItem,
  isBusinessPhoneVerificationRequired: typeof isBusinessPhoneVerificationRequired,
  isInitialized: boolean,
  loadOnboardingStatus: typeof loadOnboardingStatus,
  state: OnboardingStatusStoreState,
}

const state: OnboardingStatusStoreState = reactive({
  error: null,
  isInitialized: false,
  isLoading: false,
  onboardingStatus: null,
});

async function loadOnboardingStatus (): Promise<void> {
  state.isLoading = true;

  try {
    const onboarding = await onboardingService.getOnboardingStatus();
    state.onboardingStatus = onboarding;

  } catch (error) {
    state.error = error as OnboardingApiError;
  } finally {
    state.isLoading = false;
    state.isInitialized = true;
  }
}

function getProductUrl () {
  if (state.onboardingStatus?.product) {
    return OnboardedProductUrl[state.onboardingStatus?.product] || null;
  }
  return null;
}

function getNextPage () {
  if (state.onboardingStatus === null || state.error) {
    return null;
  }
  return onboardingService.getNextPage(toRaw(state.onboardingStatus));
}

function getVerificationItem (verificationItemName: OnboardingVerificationItemName) {
  return state.onboardingStatus?.verifications.find(({ name }) => name === verificationItemName);
}

function getSignupVerificationItem () {
  return getVerificationItem(OnboardingVerificationItemName.SignUp);
}

function isBusinessPhoneVerificationRequired () {
  const businessPhoneVerificationItem = getVerificationItem(OnboardingVerificationItemName.BusinessPhoneVerification);
  return businessPhoneVerificationItem?.status !== OnboardingVerificationItemStatus.Completed
    && businessPhoneVerificationItem?.outcome !== OnboardingVerificationItemOutcome.Approved;
}

async function getElapsedSecondsSinceSignup () {
  try {
    const signupVerificationItem = getSignupVerificationItem();
    if (!signupVerificationItem || !signupVerificationItem.createdAt) {
      return undefined;
    }
    return await serverTimeService.getElapsedSecondsSince(signupVerificationItem.createdAt);
  } catch (error) {
    return undefined;
  }
}

export default {
  get business () {
    return state.onboardingStatus?.business || null;
  },
  get error () {
    return state.error;
  },
  getElapsedSecondsSinceSignup,
  getNextPage,
  getProductUrl,
  getSignupVerificationItem,
  isBusinessPhoneVerificationRequired,
  get isInitialized () {
    return state.isInitialized;
  },
  loadOnboardingStatus,
  state,
};
